import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import type { RogerDocFormTypeCatalogEntry } from '../../services/api/types.gen';
import { SelectableNestedTable } from './SelectableNestedTable';
import { useBespokeTemplatesImport } from './useBespokeTemplatesImport';

export function BespokeTemplatesImportDialog({
  open,
  onOpenChange,
  formTypeCatalog,
  catalogLoading,
  onImported,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formTypeCatalog: RogerDocFormTypeCatalogEntry[];
  catalogLoading: boolean;
  onImported: () => void;
}) {
  const {
    importFileRef,
    importPrepLoading,
    importParseError,
    importSubmitting,
    importBundleSummary,
    importSourceRows,
    importNestedGroups,
    importSelectedKeys,
    setImportSelectedKeys,
    importAllLeafKeys,
    importSelectableRows,
    handleImportBundleFileChosen,
    handleRunBespokeImport,
  } = useBespokeTemplatesImport({
    open,
    formTypeCatalog,
    onImported,
    onClose: () => onOpenChange(false),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl max-h-[min(92vh,56rem)] flex flex-col'>
        <DialogHeader>
          <DialogTitle>Import bespoke templates</DialogTitle>
          <DialogDescription>
            Choose a JSON bundle below. Each section&apos;s layout replaces the
            one in this environment, or is added if the section has none.
          </DialogDescription>
        </DialogHeader>
        <input
          ref={importFileRef}
          type='file'
          accept='application/json,.json'
          className='hidden'
          onChange={e => void handleImportBundleFileChosen(e)}
        />

        <div className='shrink-0 flex flex-wrap items-center gap-2'>
          <Button
            type='button'
            variant='outline'
            size='sm'
            disabled={
              catalogLoading || importSubmitting || formTypeCatalog.length === 0
            }
            onClick={() => importFileRef.current?.click()}
          >
            Choose JSON bundle…
          </Button>
          {importBundleSummary && (
            <span className='text-xs text-muted-foreground truncate max-w-[14rem] sm:max-w-xs'>
              {importBundleSummary.filename}
            </span>
          )}
        </div>

        {importParseError ? (
          <div className='rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive'>
            {importParseError}
          </div>
        ) : null}

        {importBundleSummary ? (
          <div className='text-xs text-muted-foreground space-y-0.5'>
            <div>
              <span className='font-medium text-foreground'>Bundle</span>
              {importBundleSummary.exportScope != null &&
              importBundleSummary.exportScope !== ''
                ? ` · scope: ${importBundleSummary.exportScope}`
                : ''}
              {importBundleSummary.exportedAt
                ? ` · exported ${importBundleSummary.exportedAt}`
                : ''}
            </div>
            <div>
              <span className='font-medium text-foreground'>
                {importBundleSummary.rowCount}
              </span>{' '}
              row(s) queued
            </div>
          </div>
        ) : (
          !importParseError && (
            <p className='text-sm text-muted-foreground'>
              Pick a bundle to preview grouped rows before importing.
            </p>
          )
        )}

        {importBundleSummary &&
        !importParseError &&
        importSourceRows.length > 0 ? (
          <div className='min-h-0 flex flex-1 flex-col gap-2'>
            {importPrepLoading ||
            formTypeCatalog.length === 0 ||
            importNestedGroups.length === 0 ? (
              <div
                className='flex min-h-[12rem] max-h-[min(48vh,24rem)] flex-col items-center justify-center gap-2 rounded-md border border-border bg-muted/30 text-sm text-muted-foreground'
                role='status'
                aria-busy='true'
                aria-live='polite'
              >
                <div className='flex items-center gap-2'>
                  <Loader2
                    className='h-5 w-5 shrink-0 animate-spin'
                    aria-hidden
                  />
                  <span>Loading schema versions for grouping…</span>
                </div>
              </div>
            ) : (
              <>
                <div className='flex flex-wrap gap-2'>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    className='h-8 px-2'
                    disabled={
                      importSubmitting || importAllLeafKeys.length === 0
                    }
                    onClick={() =>
                      setImportSelectedKeys(new Set(importAllLeafKeys))
                    }
                  >
                    Select all rows
                  </Button>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    className='h-8 px-2'
                    disabled={importSubmitting}
                    onClick={() => setImportSelectedKeys(new Set())}
                  >
                    Clear all rows
                  </Button>
                </div>
                <SelectableNestedTable
                  className='min-h-[12rem] max-h-[min(48vh,24rem)] overflow-y-auto overflow-x-auto'
                  rows={importSelectableRows}
                  selectedKeys={importSelectedKeys}
                  setSelectedKeys={setImportSelectedKeys}
                  disabled={importSubmitting}
                />
              </>
            )}
          </div>
        ) : null}

        <DialogFooter className='shrink-0'>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={importSubmitting}
          >
            Cancel
          </Button>
          <Button
            type='button'
            onClick={() => void handleRunBespokeImport()}
            disabled={
              importPrepLoading ||
              importSubmitting ||
              importSelectedKeys.size === 0
            }
          >
            {importSubmitting && (
              <Loader2 className='h-4 w-4 mr-2 animate-spin' aria-hidden />
            )}
            Import {importSelectedKeys.size} row
            {importSelectedKeys.size === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
